import React, { FC, useCallback } from 'react';
import { FormComponentProps } from '../../global-forms/GlobalForms.types';
import {
  QueryFactory,
  ResourceSettingsDto,
  ResourceType,
} from '@lmt/shared-types';
import globalFormStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useModal } from '../../common/ui-kit/modal/useModal';
import { useQueryClient } from '@tanstack/react-query';
import { CheckBox } from '../../common/ui-kit/CheckBox';
import { SubmitButton } from '../../common/ui-kit/buttons/SubmitButton';

type ResourceSettingsProps = FormComponentProps & {
  resourceType: ResourceType;
};

export const ResourceSettingsForm: FC<ResourceSettingsProps> = (props) => {
  const { formType, formId, tryCompleteForm, resourceType } = props;
  const i18next = useScopedTranslation('resources.settings');
  const queryClient = useQueryClient();
  const modals = useModal();

  const ResourceSettingsQuery =
    QueryFactory.ResourceSettingsQuery.useGetQuery();

  const form = useAdvancedForm<ResourceSettingsDto>(
    useCallback(
      async (data) => {
        try {
          await tryCompleteForm(
            formType,
            formId,
            async () => {
              await QueryFactory.ResourceSettingsClient.patch(data);
              await queryClient.invalidateQueries({
                queryKey: QueryFactory.ResourceSettingsQuery.getQueryKey(),
              });
              await queryClient.invalidateQueries({
                queryKey: QueryFactory.ResourceQuery.getResourcesQueryKey(),
              });
            },
            i18next.t('saving'),
          );
        } catch (err) {
          await modals.showError({ text: convertToErrorString(err) });
        }
      },
      [formType, formId, tryCompleteForm],
    ),
    ResourceSettingsQuery.data,
    {
      shouldFocusError: true,
      criteriaMode: 'all',
    },
  );

  return (
    <form
      onSubmit={form.handleSubmitDefault}
      className={globalFormStyles.autoSizedContainer}
      data-test-id="configureDictionaryPopup"
    >
      <div className={globalFormStyles.dataContainer}>
        {resourceType === ResourceType.Places && (
          <CheckBox
            title={i18next.t('manual_ordering')}
            {...form.register('roomsManualOrdering')}
          />
        )}
        {resourceType === ResourceType.SpecialConditions && (
          <CheckBox
            title={i18next.t('manual_ordering')}
            {...form.register('equipmentManualOrdering')}
          />
        )}
        {resourceType === ResourceType.Doctors && (
          <CheckBox
            title={i18next.t('manual_ordering')}
            {...form.register('doctorsManualOrdering')}
          />
        )}
      </div>
      <div className={globalFormStyles.submitContainer}>
        <SubmitButton
          submitting={form.formState.isSubmitting}
          className={globalFormStyles.submit}
          title={i18next.t('save_button_title')}
        />
      </div>
    </form>
  );
};
